import * as yup from 'yup';

import countries from '../../variables/countrys';
import typePersonOptions, { TypePerson } from '../../variables/type-person';
import paymentMethod, { PaymentMethod } from '../../variables/payment-method';
import paymentInterval, { PaymentInterval } from '../../variables/payment-interval';
import paymentInstallments, { PaymentInstallments } from '../../variables/payment-installments';
import payment from '../../utils/payment';
import validator from '../../utils/validator';
import Plan from '../../view-model/plans/Plan';

// TOOD: verificar tamanho limite dos campos no banco e corrigir aqui
const checkoutSchema = yup.object().shape({
    paymentMethod: yup
        .string()
        .strict(true)
        .typeError('Campo inválido.')
        .required('Campo é obrigatório.')
        .oneOf(paymentMethod.map(option => option.value), 'Campo inválido.')
        .default('credit_card'),
    installments: yup
        .number()
        .typeError('Campo inválido.')
        .required('Campo é obrigatório.')
        .oneOf(paymentInstallments.map(option => option.value), 'Campo inválido.')
        .default(1),
    interval: yup
        .string()
        .strict(true)
        .typeError('Campo inválido.')
        .required('Campo é obrigatório.')
        .oneOf(paymentInterval.map(option => option.value), 'Campo inválido.')
        .default('yearly'),
    plan: yup
        .mixed()
        .required('Campo é obrigatório.')
        .test('isValidPlan', 'Campo inválido.', value => value && typeof value.id === 'number'),
    creditCardName: yup
        .string()
        .typeError('Campo inválido.')
        .when('paymentMethod', {
            is: 'credit_card',
            then: schema => schema
                .required('Campo é obrigatório.')
                .test('isValidCardName', 'Campo inválido.', value => {
                    if (!value)
                        return false;

                    const names = payment.Iugu.utils.getFirstLastNameByFullName(value);

                    return names && payment.Iugu.utils.validateFirstName(names[0]) && payment.Iugu.utils.validateLastName(names[1]);
                })
        })
        .default(''),
    creditCardNumber: yup
        .string()
        .typeError('Campo inválido.')
        .when('paymentMethod', {
            is: 'credit_card',
            then: schema => schema
                .required('Campo é obrigatório.')
                .test('isValidCardNumber', 'Campo inválido.', value =>
                    value ? payment.Iugu.utils.validateCreditCardNumber(value) : false
                )
        })
        .default(''),
    creditCardCVV: yup
        .string()
        .typeError('Campo inválido.')
        .when('paymentMethod', {
            is: 'credit_card',
            then: schema => schema
                .required('Campo é obrigatório.')
                .test('isValidCVV', 'Campo inválido.', (value, context) =>
                    context.parent.creditCardNumber && value
                        ? payment.Iugu.utils.validateCVV(value, payment.Iugu.utils.getBrandByCreditCardNumber(context.parent.creditCardNumber))
                        : false
                )
        })
        .default(''),
    creditCardExpiration: yup
        .string()
        .typeError('Campo inválido.')
        .when('paymentMethod', {
            is: 'credit_card',
            then: schema => schema
                .required('Campo é obrigatório.')
                .test('isValidCardExpiration', 'Campo inválido.', value =>
                    value ? payment.Iugu.utils.validateExpirationString(value) : false
                )
        })
        .default(''),
    couponCode: yup
        .string()
        .typeError('Campo inválido.'),
    terms: yup
        .boolean()
        .typeError('É necessário aceitar os termos.')
        .required('É necessário aceitar os termos.')
        .isTrue('É necessário aceitar os termos.')
        .default(false),
    renovation: yup
        .boolean()
        .typeError('É necessário aceitar o aviso de renovação.')
        .required('É necessário aceitar o aviso de renovação.')
        .isTrue('É necessário aceitar o aviso de renovação.')
        .default(false),
    payer: yup.object().shape({
        address: yup.object().shape({
            country: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .required('Campo é obrigatório.')
                .oneOf(countries, 'Campo inválido.')
                .default('Brazil'),
            zipCode: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .when('country', {
                    is: 'Brazil',
                    then: schema => schema
                        .required('Campo é obrigatório.')
                        .matches(/[0-9]{2}\.?[0-9]{3}-?[0-9]{3}/, 'Campo inválido.')
                })
                .default(''),
            state: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .when('country', {
                    is: 'Brazil',
                    then: schema => schema.required('Campo é obrigatório.')
                })
                .default(''),
            city: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .when('country', {
                    is: 'Brazil',
                    then: schema => schema.required('Campo é obrigatório.')
                })
                .default(''),
            district: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .when('country', {
                    is: 'Brazil',
                    then: schema => schema.required('Campo é obrigatório.')
                })
                .default(''),
            address: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .when('country', {
                    is: 'Brazil',
                    then: schema => schema.required('Campo é obrigatório.')
                })
                .default(''),
            number: yup.lazy(
                value => !value || value === 's/n'
                    ? yup
                        .string()
                        .typeError('Deve ser um número válido ou "s/n".')
                        .when('country', {
                            is: 'Brazil',
                            then: schema => schema
                                .required('Deve ser um número válido ou "s/n".')
                                .oneOf(['s/n'], 'Deve ser um número válido ou "s/n".')
                        })
                    : yup
                        .number()
                        .typeError('Deve ser um número válido ou "s/n".')
                        .when('country', {
                            is: 'Brazil',
                            then: schema => schema
                                .required('Deve ser um número válido ou "s/n".')
                                .integer('Deve ser um número válido ou "s/n".')
                                .moreThan(0, 'Deve ser um número válido ou "s/n".')
                                .max(999999, 'Deve ser um número válido ou "s/n".')
                        })
            ),
            complement: yup
                .string()
                .strict(true)
                .typeError('Campo inválido.')
                .default('')
        }),
        firstName: yup
            .string()
            .strict(true)
            .typeError('Campo inválido.')
            .when('address.country', {
                is: (country: string) => country === 'Brazil',
                then: schema => schema
                    .required('Campo é obrigatório.')
                    .min(2, 'Campo inválido.')
            })
            .default(''),
        lastName: yup
            .string()
            .strict(true)
            .typeError('Campo inválido.')
            .when('address.country', {
                is: (country: string) => country === 'Brazil',
                then: schema => schema
                    .required('Campo é obrigatório.')
                    .min(2, 'Campo inválido.')
            })
            .default(''),
        typePerson: yup
            .string()
            .strict(true)
            .typeError('Campo inválido.')
            .when('address.country', {
                is: (country: string) => country === 'Brazil',
                then: schema => schema
                    .required('Campo é obrigatório.')
                    .oneOf(typePersonOptions.map(option => option.value), 'Campo inválido.')
            })
            .default('natural'),
        cpf: yup
            .string()
            .typeError('Campo inválido.')
            .when(['address.country', 'typePerson'], {
                is: (country: string, typePerson: TypePerson) => country === 'Brazil' && typePerson === 'natural',
                then: schema => schema
                    .required('Campo é obrigatório.')
                    .test('isValidCPF', 'Campo inválido.', value => typeof value === 'string' && validator.isValidCPF(value))
            })
            .default(''),
        cnpj: yup
            .string()
            .typeError('Campo inválido.')
            .when(['address.country', 'typePerson'], {
                is: (country: string, typePerson: TypePerson) => country === 'Brazil' && typePerson === 'legal',
                then: schema => schema
                    .required('Campo é obrigatório.')
                    .test('isValidCNPJ', 'Campo inválido.', value => typeof value === 'string' && validator.isValidCNPJ(value))
            })
            .default(''),
        companyName: yup
            .string()
            .typeError('Campo inválido.')
            .when(['typePerson', 'address.country'], {
                is: (typePerson: TypePerson, country: string) => typePerson === 'legal' && country === 'Brazil',
                then: schema => schema.required('Campo é obrigatório.')
            })
            .default(''),
        // foreignId: yup
        //     .string()
        //     .typeError('Campo inválido.')
        //     .when('address.country', {
        //         is: (country: string) => country !== 'Brazil',
        //         then: schema => schema.required('Campo é obrigatório.')
        //     })
        //     .default(''),
        foreignName: yup
            .string()
            .typeError('Campo inválido.')
            .when('address.country', {
                is: (country: string) => country !== 'Brazil',
                then: schema => schema.required('Campo é obrigatório.')
            })
            .default('')
    })
});

export type CheckoutValue = yup.TypeOf<typeof checkoutSchema> & {
    plan: Plan,
    interval: PaymentInterval,
    installments: PaymentInstallments,
    paymentMethod: PaymentMethod,
    payer: {
        typePerson: TypePerson,
        address: {
            number: number | 's/n'
        }
    }
};

export default checkoutSchema;
